// src/components/PerformanceValidation.jsx
import React, { useState } from 'react';
import { performanceApi } from './api';
import './PerformanceValidation.css';

const PerformanceValidation = ({ groupId, periodId, periodName, onValidationComplete }) => {
  const [validating, setValidating] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState({
    errors: true,
    warnings: true,
    info: false
  });

  const handleValidate = async () => {
    setValidating(true);
    setError(null);
    setReport(null);

    try {
      const result = await performanceApi.validatePerformancePeriod(groupId, periodId);
      setReport(result);
      
      // Notify parent component if callback provided
      if (onValidationComplete) {
        onValidationComplete(result);
      }
    } catch (err) {
      setError(err.message || 'Validation failed');
      console.error('Validation error:', err);
    } finally {
      setValidating(false);
    }
  };

  const toggleSection = (section) => {
    setShowDetails(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusIcon = () => {
    if (!report) return null;
    return report.is_valid ? '✅' : '❌';
  };

  const getStatusClass = () => {
    if (!report) return '';
    return report.is_valid ? 'status-success' : 'status-error';
  };

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h3>Performance Period Validation</h3>
        <p className="period-info">
          {periodName} • {report && new Date(report.validation_timestamp).toLocaleString()}
        </p>
      </div>

      <div className="validation-actions">
        <button 
          onClick={handleValidate}
          disabled={validating}
          className="btn-validate"
        >
          {validating ? (
            <>
              <span className="spinner"></span>
              Validating...
            </>
          ) : (
            <>
              🔍 Run Validation
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {report && (
        <div className="validation-report">
          {/* Summary Section */}
          <div className={`validation-summary ${getStatusClass()}`}>
            <div className="summary-header">
              <h4>
                {getStatusIcon()} Validation {report.is_valid ? 'Passed' : 'Failed'}
              </h4>
            </div>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">Errors:</span>
                <span className={`stat-value ${report.summary.total_errors > 0 ? 'text-error' : 'text-success'}`}>
                  {report.summary.total_errors}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Warnings:</span>
                <span className={`stat-value ${report.summary.total_warnings > 0 ? 'text-warning' : 'text-success'}`}>
                  {report.summary.total_warnings}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Can Close Period:</span>
                <span className={`stat-value ${report.summary.can_close_period ? 'text-success' : 'text-error'}`}>
                  {report.summary.can_close_period ? 'YES ✓' : 'NO ✗'}
                </span>
              </div>
            </div>
          </div>

          {/* Info Messages */}
          {report.info && report.info.length > 0 && (
            <div className="validation-section">
              <div 
                className="section-header"
                onClick={() => toggleSection('info')}
              >
                <h5>
                  <span className="toggle-icon">{showDetails.info ? '▼' : '▶'}</span>
                  ℹ️ Information ({report.info.length})
                </h5>
              </div>
              {showDetails.info && (
                <div className="section-content info-section">
                  {report.info.map((msg, index) => (
                    <div key={index} className="message-item info-item">
                      {msg.includes('===') ? (
                        <strong>{msg}</strong>
                      ) : (
                        <span>{msg}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Warnings Section */}
          {report.warnings && report.warnings.length > 0 && (
            <div className="validation-section">
              <div 
                className="section-header warning-header"
                onClick={() => toggleSection('warnings')}
              >
                <h5>
                  <span className="toggle-icon">{showDetails.warnings ? '▼' : '▶'}</span>
                  ⚠️ Warnings ({report.warnings.length})
                </h5>
              </div>
              {showDetails.warnings && (
                <div className="section-content warning-section">
                  {report.warnings.map((warning, index) => (
                    <div key={index} className="message-item warning-item">
                      <span className="item-number">{index + 1}.</span>
                      <span className="item-text">{warning}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Errors Section */}
          {report.errors && report.errors.length > 0 && (
            <div className="validation-section">
              <div 
                className="section-header error-header"
                onClick={() => toggleSection('errors')}
              >
                <h5>
                  <span className="toggle-icon">{showDetails.errors ? '▼' : '▶'}</span>
                  ❌ Errors ({report.errors.length})
                </h5>
              </div>
              {showDetails.errors && (
                <div className="section-content error-section">
                  {report.errors.map((error, index) => (
                    <div key={index} className="message-item error-item">
                      <span className="item-number">{index + 1}.</span>
                      <span className="item-text">{error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Guidance */}
          <div className="validation-guidance">
            {report.is_valid ? (
              <div className="guidance-success">
                <p>✅ All checks passed! This period can be safely closed.</p>
              </div>
            ) : (
              <div className="guidance-error">
                <p>❌ Validation failed. Please fix the errors above before closing this period.</p>
                <p className="guidance-hint">
                  <strong>Tip:</strong> Start by addressing the errors first, then review warnings.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceValidation;